import { createServerFn } from '@tanstack/react-start'
import { getRequestHeaders } from '@tanstack/react-start/server'
import { and, eq } from 'drizzle-orm'

import { auth } from '@/lib/auth/auth'
import { db } from '@/lib/db'
import {
  messengerState,
  messengerContact,
  type MessengerProvider,
} from '@/lib/db/schema'

import { copy } from './copy'
import { evaluateLinkToken, type LinkTokenVerdict } from './link-token-rules'
import {
  getLinkToken,
  getDmMessengerState,
  linkMessengerAccount,
  unlinkMessengerAccount,
} from './linking'
import { transportFor } from './transports'
import { sendWelcome } from './welcome'

export type LinkTokenInfo =
  | {
      status: 'ok'
      provider: MessengerProvider
      displayName: string | null
      userEmail: string
    }
  | { status: Exclude<LinkTokenVerdict, 'ok'> | 'unauthorized' }

export const getLinkTokenInfoFn = createServerFn({ method: 'GET' })
  .validator((data: { token: string }) => data)
  .handler(async ({ data }): Promise<LinkTokenInfo> => {
    const session = await auth.api.getSession({ headers: getRequestHeaders() })
    if (!session?.user) {
      return { status: 'unauthorized' }
    }
    const row = await getLinkToken(data.token)
    const verdict = evaluateLinkToken(row)
    if (verdict !== 'ok' || !row) {
      return { status: verdict === 'ok' ? 'not_found' : verdict }
    }
    return {
      status: 'ok',
      provider: row.provider,
      displayName: row.displayName,
      userEmail: session.user.email,
    }
  })

export type ConfirmLinkResult =
  | { status: 'linked'; provider: MessengerProvider }
  | { status: 'not_found' | 'expired' | 'used' | 'conflict' | 'unauthorized' }

export const confirmLinkFn = createServerFn({ method: 'POST' })
  .validator((data: { token: string }) => data)
  .handler(async ({ data }): Promise<ConfirmLinkResult> => {
    const session = await auth.api.getSession({ headers: getRequestHeaders() })
    if (!session?.user) {
      return { status: 'unauthorized' }
    }
    const result = await linkMessengerAccount({
      token: data.token,
      userId: session.user.id,
    })
    if (!result.ok) {
      return { status: result.reason }
    }
    // Notify the messenger side (best-effort)
    sendWelcome(
      transportFor(result.provider),
      { platformChatId: result.platformChatId },
      {
        userId: session.user.id,
        source: 'account_linked',
        confirmation: true,
      },
    ).catch((error: unknown) => {
      console.error('[link] failed to notify messenger:', error)
    })
    return { status: 'linked', provider: result.provider }
  })

export interface LinkedMessenger {
  provider: MessengerProvider
  linkedAt: string
}

export const getLinkedMessengersFn = createServerFn({ method: 'GET' }).handler(
  async (): Promise<LinkedMessenger[]> => {
    const session = await auth.api.getSession({ headers: getRequestHeaders() })
    if (!session?.user) {
      return []
    }
    const rows = await db
      .select({
        provider: messengerState.provider,
        platformChatId: messengerContact.platformChatId,
      })
      .from(messengerState)
      .innerJoin(
        messengerContact,
        and(
          eq(messengerContact.userId, messengerState.userId),
          eq(messengerContact.provider, messengerState.provider),
        ),
      )
      .where(eq(messengerState.userId, session.user.id))
      .groupBy(messengerState.provider, messengerContact.platformChatId)

    return rows.map((row) => ({
      provider: row.provider,
      linkedAt: new Date().toISOString(),
    }))
  },
)

export type UnlinkMessengerResult = {
  status: 'unlinked' | 'unauthorized' | 'not_linked'
}

export const unlinkMessengerFn = createServerFn({ method: 'POST' })
  .validator((data: { provider: MessengerProvider }) => data)
  .handler(async ({ data }): Promise<UnlinkMessengerResult> => {
    const session = await auth.api.getSession({ headers: getRequestHeaders() })
    if (!session?.user) {
      return { status: 'unauthorized' }
    }
    const state = await getDmMessengerState(session.user.id, data.provider)
    if (!state) {
      return { status: 'not_linked' }
    }
    await unlinkMessengerAccount(session.user.id, data.provider)
    // Tell the chat it was unlinked (best-effort)
    transportFor(data.provider)
      .sendText({ platformChatId: state.platformChatId }, copy.unlinked)
      .catch((error: unknown) => {
        console.error('[link] failed to notify messenger about unlink:', error)
      })
    return { status: 'unlinked' }
  })
